import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API_BASE } from "../api";
import styles from "../styles/Tennis.module.css";

const localDate = (value = new Date()) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const moveDate = (date, amount) => { const next = new Date(`${date}T12:00:00`); next.setDate(next.getDate() + amount); return localDate(next); };
const dateLabel = (date) => new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${date}T12:00:00`));
const timeLabel = (iso) => new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZoneName: "short" }).format(new Date(iso));

function PlayerRow({ player, leader }) {
  return <div className={styles.playerRow}>
    <div className={styles.playerIdentity}>
      {player.headshot ? <img className={styles.headshot} src={player.headshot} alt="" /> : <div className={styles.headshotFallback}>{String(player.name || "?").charAt(0)}</div>}
      <div className={styles.playerCopy}>
        <div className={styles.playerNameLine}><strong>{player.name}</strong>{player.flag ? <img className={styles.flag} src={player.flag} alt={player.country || ""} /> : null}</div>
        <span>{player.rank ? `World No. ${player.rank}` : "Unranked"} · {Math.round(player.ranking_points || 0).toLocaleString()} pts</span>
      </div>
    </div>
    <div className={`${styles.fairPrice} ${leader ? styles.fairPriceLeader : ""}`}><strong>{Math.round(Number(player.probability || 0) * 100)}%</strong><span>Fair {Number(player.fair_odds || 0).toFixed(2)}</span></div>
  </div>;
}

function MatchCard({ match }) {
  const [open, setOpen] = useState(false);
  const firstLeader = Number(match.player_one?.probability) >= Number(match.player_two?.probability);
  return <article className={styles.matchCard}>
    <button className={styles.cardButton} type="button" onClick={() => setOpen((value) => !value)}>
      <div className={styles.matchHeader}>
        <div className={styles.badges}><span className={`${styles.tourBadge} ${match.tour === "WTA" ? styles.wtaBadge : ""}`}>{match.tour}</span><span>{match.tournament}</span>{match.round ? <span>· {match.round}</span> : null}</div>
        <div className={styles.time}>{timeLabel(match.kickoff_utc)} <span className={open ? styles.chevronOpen : styles.chevron}>⌄</span></div>
      </div>
      <PlayerRow player={match.player_one} leader={firstLeader} /><div className={styles.divider} /><PlayerRow player={match.player_two} leader={!firstLeader} />
    </button>
    {open ? <div className={styles.explanation}>
      <strong>◎ {String(match.confidence || "low").toUpperCase()} MODEL CONFIDENCE</strong>
      {(match.reasons || []).map((reason) => <p key={reason}>• {reason}</p>)}
      <div className={styles.compareHint}>Compare our fair price with your bookmaker. A larger bookmaker price may warrant further research—it is not an automatically tracked edge.</div>
    </div> : null}
  </article>;
}

export default function Tennis() {
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || localDate();
  const tour = params.get("tour") || "ALL";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false; setLoading(true); setError("");
    fetch(`${API_BASE}/tennis/research?date=${encodeURIComponent(date)}`, { cache: "no-store" })
      .then(async (response) => { const payload = await response.json().catch(() => ({})); if (!response.ok) throw new Error(payload?.detail || `Tennis feed returned ${response.status}`); return payload; })
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch((err) => { if (!cancelled) setError(err.message || "Tennis research is unavailable."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  const matches = useMemo(() => (data?.matches || []).filter((match) => tour === "ALL" || match.tour === tour), [data, tour]);
  const update = (key, value) => { const next = new URLSearchParams(params); next.set(key, value); setParams(next, { replace: true }); };

  return <div className={styles.page}>
    <header className={styles.hero}><div><div className={styles.eyebrow}>CSB RESEARCH LAB · US OPEN</div><h1>Tennis fair prices</h1><p>Independent win probabilities built from ranking strength and tournament form—without bookmaker prices influencing the model.</p></div><div className={styles.heroMark}>◎</div></header>
    <section className={styles.toolbar}>
      <button type="button" onClick={() => update("date", moveDate(date, -1))}>←</button>
      <label><span>MATCH CARD</span><input type="date" value={date} onChange={(event) => update("date", event.target.value)} /><strong>{dateLabel(date)}</strong></label>
      <button type="button" onClick={() => update("date", moveDate(date, 1))}>→</button>
    </section>
    <div className={styles.filters}>{[["ALL", "All singles"], ["ATP", "ATP"], ["WTA", "WTA"]].map(([value, label]) => <button className={tour === value ? styles.activeFilter : ""} key={value} type="button" onClick={() => update("tour", value)}>{label}</button>)}</div>
    {loading ? <div className={styles.stateCard}>Building today’s research card…</div> : null}
    {error ? <div className={`${styles.stateCard} ${styles.error}`}>{error}</div> : null}
    {!loading && !error && !matches.length ? <div className={styles.stateCard}>No singles matches found for this day.</div> : null}
    <div className={styles.matchGrid}>{matches.map((match) => <MatchCard key={match.id} match={match} />)}</div>
    <footer className={styles.disclaimer}><strong>Research estimates only.</strong> These are not published picks, captured edges or CLV. Rankings and schedules come from a cached public feed and can be delayed.</footer>
  </div>;
}
